import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';
import { useTypes } from '../../hooks/useVault';
import { GraphCanvas } from './GraphCanvas';
import type { GraphPayload } from '@cortex/shared';

function syntheticGraph(n: number): GraphPayload {
  const types = ['note', 'site', 'device', 'link', 'runbook', 'case', 'reference'];
  const nodes = Array.from({ length: n }, (_, i) => ({ id: `n${i}`, path: `bench/n${i}.md`, title: `Node ${i}`, type: types[i % types.length], degree: 0 }));
  const edges: GraphPayload['edges'] = [];
  for (let i = 1; i < n; i++) {
    // Preferential-ish attachment: hubs emerge, like a real vault.
    const t = i < 50 ? Math.floor(Math.random() * i) : Math.floor(Math.random() * Math.min(i, 200)) + (Math.random() < 0.7 ? 0 : Math.floor(Math.random() * i));
    const to = Math.min(i - 1, t);
    edges.push({ id: `e${i}`, from: `n${i}`, to: `n${to}`, toPath: null, kind: i % 5 === 0 ? 'TYPED' : 'WIKILINK', context: null });
    nodes[i].degree++;
    nodes[to].degree++;
  }
  return { nodes, edges };
}

/** Global and entity graph views with a persisted layout (`.cortex/graph-layout*.json`). */
export function GraphView() {
  const mode = useUi((s) => s.graphMode);
  const setGraphMode = useUi((s) => s.setGraphMode);
  const openNote = useUi((s) => s.openNote);
  const toast = useUi((s) => s.toast);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [unresolved, setUnresolved] = useState(false);
  const types = useTypes();
  const qc = useQueryClient();

  // Dev bench: ?graphBench=10000 renders a synthetic graph so the 45 fps @ 10k target can be measured.
  const bench = Number(new URLSearchParams(location.search).get('graphBench') ?? 0);
  const data = useQuery({
    queryKey: ['graph', mode, typeFilter, unresolved, bench],
    queryFn: () => (bench > 0 ? Promise.resolve(syntheticGraph(bench)) : mode === 'entity' ? Api.entities.graph() : Api.graph.global({ type: typeFilter || undefined, includeUnresolved: unresolved })),
    staleTime: 30_000,
  });
  const layout = useQuery({ queryKey: ['graph-layout', mode], queryFn: () => Api.graph.layout(mode), staleTime: Infinity });
  const saveTimer = useRef<number | null>(null);
  const onPositions = useCallback(
    (positions: Record<string, [number, number]>) => {
      if (typeFilter) return; // a filtered view must not overwrite the full layout
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        void Api.graph.saveLayout(mode, positions).then(() => qc.setQueryData(['graph-layout', mode], { positions, view: mode, savedAt: new Date().toISOString() })).catch(() => undefined);
      }, 1500);
    },
    [mode, typeFilter, qc],
  );
  useEffect(() => () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); }, []);

  const clusters = useMemo(() => (mode === 'entity' && data.data && 'clusters' in data.data ? (data.data as { clusters: Record<string, string> }).clusters : undefined), [mode, data.data]);
  const highlight = useUi((s) => s.panes.find((p) => p.id === s.activePaneId)?.noteId ?? null);

  return (
    <div className="graph-view">
      <div className="graph-toolbar">
        <div className="modes">
          <button className={mode === 'global' ? 'on' : ''} onClick={() => setGraphMode('global')}>Global</button>
          <button className={mode === 'entity' ? 'on' : ''} onClick={() => setGraphMode('entity')} title="Sites, devices, links, circuits">Entity topology</button>
        </div>
        {mode === 'global' && (
          <>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="">All types</option>
              {(types.data ?? []).map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <label className="row muted" style={{ fontSize: '0.8rem' }}>
              <input type="checkbox" checked={unresolved} onChange={(e) => setUnresolved(e.target.checked)} /> show unresolved links
            </label>
          </>
        )}
        <span className="spacer" />
        <span className="mono faint">
          {data.data ? `${data.data.nodes.length} nodes · ${data.data.edges.length} edges` : '…'}
          {layout.data?.savedAt ? ` · layout cached ${new Date(layout.data.savedAt).toLocaleTimeString()}` : ' · no cached layout'}
        </span>
        <button className="ghost" onClick={() => { void Api.graph.saveLayout(mode, {}).then(() => { qc.invalidateQueries({ queryKey: ['graph-layout', mode] }); qc.invalidateQueries({ queryKey: ['graph'] }); toast('Layout reset'); }); }}>
          Re-layout
        </button>
        <span className="faint" style={{ fontSize: '0.75rem' }}>double-click to fit · Ctrl+click opens in split</span>
      </div>
      {data.data && layout.data ? (
        <GraphCanvas
          data={data.data}
          clusters={clusters}
          positions={layout.data.positions}
          onPositions={onPositions}
          onOpen={(id, split) => openNote(id, { split })}
          highlightId={highlight}
        />
      ) : (
        <div className="dock-empty">{data.isError ? 'Graph unavailable' : 'Loading graph…'}</div>
      )}
    </div>
  );
}
